import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { extractApiErrorMessage } from '../../lib/api-error';

type PassName = 'semantic' | 'performance' | 'compliance' | 'synthesis' | 'all';

interface RunResponse {
  ok: boolean;
  runId: string;
  passName: PassName;
  output: Record<string, unknown>;
  passMetrics: Record<string, unknown>;
  costMicros: number;
  durationMs: number;
  error: string | null;
}

interface RunListItem {
  id: string;
  passName: PassName;
  model: string | null;
  inputTranscriptPreview: string;
  inputTranscriptLength: number;
  inputContext: Record<string, unknown>;
  costMicros: number;
  durationMs: number;
  error: string | null;
  createdAt: string;
}

interface RunDetail extends RunListItem {
  inputTranscript: string;
  output: Record<string, unknown>;
  passMetrics: Record<string, unknown>;
}

const PASS_OPTIONS: Array<{ value: PassName; label: string }> = [
  { value: 'semantic', label: 'Pass 1 — Semantic' },
  { value: 'performance', label: 'Pass 2 — Performance' },
  { value: 'compliance', label: 'Pass 3 — Compliance' },
  { value: 'synthesis', label: 'Pass 4 — Synthesis' },
  { value: 'all', label: 'All 4 passes (full pipeline)' },
];

function formatCost(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(4)}`;
}
function formatDuration(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export function AiPromptSandbox(): JSX.Element {
  const qc = useQueryClient();
  const [passName, setPassName] = useState<PassName>('semantic');
  const [transcript, setTranscript] = useState('');
  const [leadFirstName, setLeadFirstName] = useState('');
  const [leadLastName, setLeadLastName] = useState('');
  const [leadCompany, setLeadCompany] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [systemPromptOverride, setSystemPromptOverride] = useState('');
  const [salesPlaybookOverride, setSalesPlaybookOverride] = useState('');
  const [complianceRulesOverride, setComplianceRulesOverride] = useState('');
  const [modelOverride, setModelOverride] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ['ai-test-runs'],
    queryFn: () => api.get<{ items: RunListItem[] }>('/ai/test-pass/runs?limit=20'),
  });

  async function handleRun(): Promise<void> {
    if (!transcript.trim()) {
      setRunError('Paste a transcript first.');
      return;
    }
    setRunning(true);
    setRunError(null);
    setResult(null);
    try {
      const body: Record<string, unknown> = {
        passName,
        transcript,
      };
      if (leadFirstName || leadLastName || leadCompany) {
        body.lead = {
          firstName: leadFirstName || 'Test',
          lastName: leadLastName || 'Prospect',
          company: leadCompany || null,
        };
      }
      if (campaignName) body.campaignName = campaignName;
      if (systemPromptOverride.trim()) body.systemPromptOverride = systemPromptOverride;
      if (salesPlaybookOverride.trim()) body.salesPlaybookOverride = salesPlaybookOverride;
      if (complianceRulesOverride.trim()) body.complianceRulesOverride = complianceRulesOverride;
      if (modelOverride.trim()) body.modelOverride = modelOverride;
      const res = await api.post<RunResponse>('/ai/test-pass', body);
      setResult(res);
      void qc.invalidateQueries({ queryKey: ['ai-test-runs'] });
    } catch (err) {
      setRunError(extractApiErrorMessage(err, 'Run failed.'));
    } finally {
      setRunning(false);
    }
  }

  async function handleLoadRun(id: string): Promise<void> {
    try {
      const detail = await api.get<RunDetail>(`/ai/test-pass/runs/${id}`);
      setPassName(detail.passName);
      setTranscript(detail.inputTranscript);
      const ctx = detail.inputContext as Record<string, unknown>;
      const lead = ctx.lead as { firstName?: string; lastName?: string; company?: string | null } | null;
      setLeadFirstName(lead?.firstName ?? '');
      setLeadLastName(lead?.lastName ?? '');
      setLeadCompany(lead?.company ?? '');
      setCampaignName((ctx.campaignName as string) ?? '');
      setModelOverride(detail.model ?? '');
      setResult({
        ok: detail.error === null,
        runId: detail.id,
        passName: detail.passName,
        output: detail.output,
        passMetrics: detail.passMetrics,
        costMicros: detail.costMicros,
        durationMs: detail.durationMs,
        error: detail.error,
      });
    } catch (err) {
      setRunError(extractApiErrorMessage(err, 'Could not load run.'));
    }
  }

  return (
    <section className="border-t border-ink-200 pt-4 space-y-3">
      <header>
        <h3 className="text-sm font-semibold text-ink-900">Prompt sandbox</h3>
        <p className="text-xs text-ink-500 mt-0.5">
          Paste a transcript and run any pass against the live prompts (saved overrides) — or override inline to
          try a tweak before saving. Nothing here writes to CallAnalysis. Last 20 runs persist per admin.
        </p>
      </header>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Pass</label>
          <select
            className="input"
            value={passName}
            onChange={(e) => setPassName(e.target.value as PassName)}
          >
            {PASS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Model override (optional)</label>
          <input
            className="input"
            value={modelOverride}
            onChange={(e) => setModelOverride(e.target.value)}
            placeholder="leave blank for the configured model"
          />
        </div>
        <div>
          <label className="label">Lead first name</label>
          <input className="input" value={leadFirstName} onChange={(e) => setLeadFirstName(e.target.value)} />
        </div>
        <div>
          <label className="label">Lead last name</label>
          <input className="input" value={leadLastName} onChange={(e) => setLeadLastName(e.target.value)} />
        </div>
        <div>
          <label className="label">Lead company</label>
          <input className="input" value={leadCompany} onChange={(e) => setLeadCompany(e.target.value)} />
        </div>
        <div>
          <label className="label">Campaign name</label>
          <input className="input" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="label">Transcript</label>
        <textarea
          className="input font-mono text-xs leading-relaxed"
          rows={10}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder="Agent: Hi, this is Sarah from Maratic.&#10;Prospect: Yeah, hi, we're looking at..."
        />
      </div>

      <details className="rounded border border-ink-200 bg-white">
        <summary className="cursor-pointer px-3 py-2 text-sm">Inline overrides (advanced)</summary>
        <div className="border-t border-ink-100 p-3 space-y-2">
          <div>
            <label className="label">System prompt override (only applies to selected pass)</label>
            <textarea
              className="input font-mono text-xs"
              rows={6}
              value={systemPromptOverride}
              onChange={(e) => setSystemPromptOverride(e.target.value)}
              placeholder="leave blank to use the saved override / baked-in default"
            />
          </div>
          <div>
            <label className="label">Sales playbook override</label>
            <textarea
              className="input"
              rows={4}
              value={salesPlaybookOverride}
              onChange={(e) => setSalesPlaybookOverride(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Compliance rules override</label>
            <textarea
              className="input"
              rows={4}
              value={complianceRulesOverride}
              onChange={(e) => setComplianceRulesOverride(e.target.value)}
            />
          </div>
        </div>
      </details>

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn-primary" onClick={() => void handleRun()} disabled={running}>
          {running ? 'Running…' : 'Run pass'}
        </button>
        {runError && <p className="text-sm text-red-600">{runError}</p>}
      </div>

      {result && (
        <div className="rounded border border-ink-200 bg-ink-50 p-3 space-y-2">
          <div className="flex items-center gap-3 text-xs flex-wrap">
            <span className={`font-medium ${result.ok ? 'text-emerald-700' : 'text-red-700'}`}>
              {result.ok ? '✓ Success' : '✗ Failed'}
            </span>
            <span className="text-ink-600">
              {result.passName} · {formatCost(result.costMicros)} · {formatDuration(result.durationMs)}
            </span>
            <span className="text-ink-400 font-mono text-[10px]">{result.runId}</span>
          </div>
          {result.error && <p className="text-xs text-red-600">{result.error}</p>}
          <details>
            <summary className="cursor-pointer text-xs text-ink-500">Output JSON</summary>
            <pre className="mt-1 max-h-96 overflow-y-auto rounded bg-white p-2 text-[11px] font-mono whitespace-pre-wrap">
              {JSON.stringify(result.output, null, 2)}
            </pre>
          </details>
          <details>
            <summary className="cursor-pointer text-xs text-ink-500">Per-pass metrics</summary>
            <pre className="mt-1 max-h-72 overflow-y-auto rounded bg-white p-2 text-[11px] font-mono whitespace-pre-wrap">
              {JSON.stringify(result.passMetrics, null, 2)}
            </pre>
          </details>
        </div>
      )}

      <div className="border-t border-ink-100 pt-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2">
          Recent runs
        </h4>
        {runsQuery.isLoading && <p className="text-xs text-ink-400">Loading…</p>}
        {runsQuery.data?.items.length === 0 && (
          <p className="text-xs text-ink-400">No runs yet. Run a pass above to start building history.</p>
        )}
        <ul className="space-y-1.5">
          {runsQuery.data?.items.map((run) => (
            <li key={run.id} className="flex items-center gap-2 text-xs">
              <button
                type="button"
                className="text-left flex-1 truncate hover:underline"
                onClick={() => void handleLoadRun(run.id)}
                title="Load this run back into the form"
              >
                <span className="font-medium">{run.passName}</span>
                <span className="text-ink-500"> · {new Date(run.createdAt).toLocaleString()}</span>
                <span className="text-ink-400"> · {formatCost(run.costMicros)} · {formatDuration(run.durationMs)}</span>
                {run.error && <span className="text-red-600"> · failed</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
